// CrewMessagesLauncher — the crew DM entry point in the app header (roadmap #6, PM epic — crew DM). Shown only
// inside an event workspace (there IS a current event); it owns ONE authenticated SSE stream for that event so
// it can (a) light an unread dot when a private crew message arrives while the drawer is closed, and (b) feed
// live messages to the open CrewMessagesDrawer. The stream is the same ticketed host/crew stream the comments
// drawer uses (CommentService.streamTicket → /comments/stream?ticket=…); the hub delivers a private DM frame
// only to this admin (the ticket carries the userId → their user:<userId> principal). See [[eventsmagic-comments]].
import { useCallback, useEffect, useRef, useState } from "react";
import { ActionIcon, Indicator, Tooltip } from "@mantine/core";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { AppIcon } from "@/components/generic";
import { appEnv } from "@/config/branding";
import { useCurrentEvent } from "@/contexts";
import { CommentService } from "@/utils/api";
import { openTicketedStream } from "@/utils/ticketedStream";

import { CrewMessagesDrawer, type CrewDmFrame } from "./CrewMessagesDrawer";

/** Only mounts the stream + button when the user is inside an event workspace (keyed by event so a switch
 *  tears down the old stream and opens a fresh one). */
export function CrewMessagesLauncher() {
  const { currentEventId } = useCurrentEvent();
  if (!currentEventId) {
    return null;
  }

  return <CrewMessagesLauncherInner key={currentEventId} eventId={currentEventId} />;
}

function CrewMessagesLauncherInner({ eventId }: { eventId: string }) {
  const [opened, setOpened] = useState(false);
  const [hasNew, setHasNew] = useState(false);

  // The set of live-frame subscribers (the open drawer registers one). A ref so the stream handler always
  // reaches the current set without re-opening the connection.
  const listenersRef = useRef<Set<(frame: CrewDmFrame) => void>>(new Set());
  const openedRef = useRef(opened);
  openedRef.current = opened;
  // The current stream ticket — reused to POST outgoing typing pings (no need to mint a fresh one each keystroke).
  const ticketRef = useRef<string | null>(null);

  const subscribeDm = useCallback((handler: (frame: CrewDmFrame) => void) => {
    listenersRef.current.add(handler);
    return () => {
      listenersRef.current.delete(handler);
    };
  }, []);

  // Signal "the admin is typing" into a crew conversation — fire-and-forget, using the stream ticket. The server
  // delivers it only to that conversation's other members.
  const sendTyping = useCallback(
    (conversationId: string) => {
      const base = (appEnv.rootApiUrl || "").replace(/\/$/, "");
      const ticket = ticketRef.current;
      if (!base || !ticket || !conversationId || typeof window === "undefined") {
        return;
      }
      const url = `${base}/comments/stream?type=typing&ticket=${encodeURIComponent(ticket)}&conversationId=${encodeURIComponent(conversationId)}`;
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url);
        } else {
          void fetch(url, { method: "POST", keepalive: true });
        }
      } catch {
        // ignore — typing pings are best-effort
      }
    },
    []
  );

  // Opening the drawer clears the unread dot.
  useEffect(() => {
    if (opened) {
      setHasNew(false);
    }
  }, [opened]);

  // ONE authenticated SSE stream for this event, open for as long as the launcher is mounted (i.e. the whole
  // event workspace). Held by `openTicketedStream`, which re-mints the ticket and reconnects when the old one stops
  // being accepted — a stream ticket lives 30 minutes, and a stream reopened with an expired one used to go silently
  // dead (no messages, no "is typing") until a full page reload (owner, 2026-10-07).
  useEffect(() => {
    const base = (appEnv.rootApiUrl || "").replace(/\/$/, "");

    return openTicketedStream({
      base,
      path: "/comments/stream",
      mintTicket: async () => {
        const res = await CommentService.streamTicket(eventId);

        return res.code === OP_RESULT_CODES.OK && Array.isArray(res.data) ? (res.data[0] as { ticket?: string })?.ticket ?? null : null;
      },
      onTicket: (ticket) => {
        ticketRef.current = ticket;
      },
      // A reconnect may have missed frames (the crew stream does not replay) — the open drawer refetches.
      onOpen: (isReconnect) => {
        if (isReconnect) {
          for (const handler of listenersRef.current) {
            handler({ type: "resync", entityKind: "dm" });
          }
        }
      },
      onFrame: (frame: CrewDmFrame) => {
        // Only private crew DMs concern this launcher (the crew stream also carries ordinary comment events). A crew DM
        // is a 'dm' channel, so its frames carry entityKind='dm' — a new message, a "typing" ping, or a reaction change.
        const isDmMessage = frame?.type === "comment.created" && !!frame.comment?.id;
        const isDmTyping = frame?.type === "typing";
        const isDmReaction = frame?.type === "reaction.updated";
        if (!frame || frame.entityKind !== "dm" || (!isDmMessage && !isDmTyping && !isDmReaction)) {
          return;
        }

        for (const handler of listenersRef.current) {
          handler(frame);
        }

        // Only a new MESSAGE arriving while the drawer is closed lights the unread dot (typing never does).
        if (isDmMessage && !openedRef.current) {
          setHasNew(true);
        }
      },
    });
  }, [eventId]);

  return (
    <>
      <Tooltip label="Messages" withArrow>
        <Indicator size={9} offset={5} disabled={!hasNew} color="red" processing withBorder>
          <ActionIcon variant="subtle" color="gray" size="lg" radius="xl" aria-label="Messages" onClick={() => setOpened(true)}>
            <AppIcon iconName="MessageCircle" size={20} />
          </ActionIcon>
        </Indicator>
      </Tooltip>

      <CrewMessagesDrawer opened={opened} onClose={() => setOpened(false)} eventId={eventId} subscribeDm={subscribeDm} sendTyping={sendTyping} />
    </>
  );
}
